<script lang="ts" setup>
import { ref } from 'vue'
import Button from '@cypress-design/vue-button'
import InstallModalBox from './InstallModalBox.vue'
import IconTechnologyTerminal from './assets/technology_terminal.svg?component'
import IconTechnologyDownload from './assets/technology_download.svg?component'
import { IconArrowSquareDown, IconCheckmarkSolid, IconGeneralClipboard } from '@cypress-design/vue-icon'

const isShowingSuccessfulCopy = ref<boolean>(false)

function copyToClipboard() {
  navigator.clipboard.writeText('npm install cypress --save-dev').then(
    () => {
      isShowingSuccessfulCopy.value = true
      setTimeout(function () {
        isShowingSuccessfulCopy.value = false
      }, 2000)
    },
    () => {
      console.log('Failed to copy to clipboard')
    },
  )
}

withDefaults(
  defineProps<{
    shadow?: boolean
    dataPrefix?: string
  }>(),
  {
    shadow: false,
    dataPrefix: '',
  },
)
</script>

<template>
  <div class="flex flex-col justify-center gap-[24px] text-center md:flex-row">
    <InstallModalBox
      title="NPM Install"
      description="Click to copy the command below to paste into your terminal:"
      :shadow="shadow"
    >
      <template #icon>
        <IconTechnologyTerminal class="h-[48px] w-[48px]" />
      </template>
      <Button
        :class="
          isShowingSuccessfulCopy
            ? '!hover:border-transparent !hover:ring-transparent !focus:border-transparent !focus:ring-transparent cursor-default overflow-hidden !bg-jade-200 transition-colors duration-500'
            : 'justify-center'
        "
        :data-cy="`${dataPrefix}install-copy`"
        class="relative w-[240px]"
        @click="copyToClipboard"
      >
        <span v-if="isShowingSuccessfulCopy" class="flex flex-nowrap items-center gap-[8px]">
          <span class="sr-only">Copied successfully!</span>
          <IconCheckmarkSolid class="ml-[96px]" stroke-color="jade-300" fill-color="jade-600" />
          <span>Copied</span>
        </span>
        <Transition name="slide-out">
          <span v-if="!isShowingSuccessfulCopy" class="slide-out-enter-to flex flex-nowrap items-center">
            <IconGeneralClipboard class="mr-2" stroke-color="indigo-200" />
            <span class="whitespace-nowrap !font-secondary">npm install cypress</span>
          </span>
        </Transition>
      </Button>
    </InstallModalBox>
    <div class="table h-[16px] w-full text-center md:h-[264px] md:w-[16px]">
      <div
        class="divider relative flex flex-row items-center justify-center align-middle text-[12px] text-gray-700 md:table-cell"
      >
        <hr
          class="bottom-[50%] left-[50%] mx-[8px] h-[1px] w-[42%] border-0 bg-gradient-to-r from-transparent to-gray-100 md:absolute md:mx-0 md:mb-[24px] md:h-[70px] md:w-[1px] md:bg-gradient-to-b"
        />
        OR
        <hr
          class="left-[50%] top-[50%] mx-[8px] h-[1px] w-[42%] border-0 bg-gradient-to-r from-gray-100 to-transparent md:absolute md:mx-0 md:mt-[24px] md:h-[70px] md:w-[1px] md:bg-gradient-to-b"
        />
      </div>
    </div>
    <InstallModalBox
      title="Direct Download"
      description="Download the Cypress application directly to give it a try:"
      :shadow="shadow"
    >
      <template #icon>
        <IconTechnologyDownload class="h-[48px] w-[48px]" />
      </template>
      <Button
        href="https://download.cypress.io/app"
        variant="outline-light"
        :data-cy="`${dataPrefix}install-download`"
        class="relative w-[240px]"
      >
        <IconArrowSquareDown class="mr-2" stroke-color="gray-500" />
        Download Cypress.zip
      </Button>
    </InstallModalBox>
  </div>
</template>
